/**
 * Editor typography: the stepped ranges the three sizing settings move
 * through, and the sanitiser for a stored blob. Swift
 * (`EditorTypographyManager.swift`) and Android (`EditorTypography.kt`)
 * mirror these tables: change the three clients together.
 */

import type { EditorTypography } from '@caliu/types'
import { DEFAULT_EDITOR_TYPOGRAPHY } from '@caliu/types'

export interface TypographyRange {
  min: number
  max: number
  step: number
}

export const EDITOR_TYPOGRAPHY_RANGES: Record<keyof EditorTypography, TypographyRange> = {
  size: { min: 12, max: 28, step: 1 },
  // Below 1.5 the native editors cannot honour the value: their line already
  // carries a fixed gap on top of the font's own height. 1.5 is also WCAG's floor.
  lineHeight: { min: 1.5, max: 2.4, step: 0.1 },
  paragraphSpacing: { min: 2, max: 16, step: 2 },
}

/** The named presets older builds stored, read once as their nearest number. */
const LEGACY_PRESETS: Record<keyof EditorTypography, Record<string, number>> = {
  size: { xs: 14, s: 15, m: 17, l: 19, xl: 22, xxl: 26 },
  lineHeight: { snug: 1.6, tight: 1.7, normal: 1.8, relaxed: 2.0, loose: 2.2 },
  paragraphSpacing: { snug: 4, tight: 6, normal: 8, relaxed: 10, loose: 14 },
}

/** Clamp to the range and snap to its step (one decimal, so 1.7 stays 1.7). */
export function snapTypographyValue(key: keyof EditorTypography, value: number): number {
  const { min, max, step } = EDITOR_TYPOGRAPHY_RANGES[key]
  const stepped = min + Math.round((value - min) / step) * step
  return Number(Math.min(max, Math.max(min, stepped)).toFixed(1))
}

/** Sanitize a stored typography blob, filling defaults for invalid keys. */
export function normalizeEditorTypography(
  raw: Partial<Record<keyof EditorTypography, unknown>> | null | undefined,
): EditorTypography {
  const read = (key: keyof EditorTypography): number => {
    const value = raw?.[key]
    const number = typeof value === 'string' ? LEGACY_PRESETS[key][value] : value
    return typeof number === 'number' && Number.isFinite(number)
      ? snapTypographyValue(key, number)
      : DEFAULT_EDITOR_TYPOGRAPHY[key]
  }
  return { size: read('size'), lineHeight: read('lineHeight'), paragraphSpacing: read('paragraphSpacing') }
}
